import { doc, getDoc } from "firebase/firestore";
import { canUserAccessFolder } from "./explorerFolderAccess.js";

export const EXPLORER_LAST_LOCATION_KEY = "cc-storage-explorer-last-location";

const VALID_ROOT_DIRS = new Set(["personal", "team"]);

function isValidLocationShape(location) {
    if (!location || typeof location !== "object" || Array.isArray(location)) {
        return false;
    }

    if (typeof location.userId !== "string" || !location.userId) {
        return false;
    }

    if (!VALID_ROOT_DIRS.has(location.rootDir)) {
        return false;
    }

    if (
        location.folderId !== null &&
        (typeof location.folderId !== "string" || !location.folderId)
    ) {
        return false;
    }

    return true;
}

export function getLastExplorerLocation(userId) {
    if (!userId) {
        return null;
    }

    try {
        const raw = localStorage.getItem(EXPLORER_LAST_LOCATION_KEY);
        if (!raw) {
            return null;
        }

        const location = JSON.parse(raw);
        if (!isValidLocationShape(location) || location.userId !== userId) {
            return null;
        }

        return {
            userId: location.userId,
            rootDir: location.rootDir,
            folderId: location.folderId ?? null,
        };
    } catch {
        return null;
    }
}

export function saveLastExplorerLocation({ userId, rootDir, folderId = null }) {
    if (!userId || !VALID_ROOT_DIRS.has(rootDir)) {
        return;
    }

    const normalizedFolderId =
        folderId === null || folderId === undefined || folderId === ""
            ? null
            : folderId;

    localStorage.setItem(
        EXPLORER_LAST_LOCATION_KEY,
        JSON.stringify({
            userId,
            rootDir,
            folderId: normalizedFolderId,
        }),
    );
}

export function saveLastExplorerLocationFromRecord(userId, record) {
    if (!userId || !record?.rootDir) {
        return;
    }

    saveLastExplorerLocation({
        userId,
        rootDir: record.rootDir,
        folderId: record.locationId ?? null,
    });
}

export async function validateExplorerLocation({
    rootDir,
    folderId = null,
    homeDbRef,
    teamDbRef,
    user = null,
}) {
    if (!VALID_ROOT_DIRS.has(rootDir)) {
        return false;
    }

    if (rootDir === "team" && !teamDbRef) {
        return false;
    }

    if (folderId === null) {
        return true;
    }

    const dbRef = rootDir === "personal" ? homeDbRef : teamDbRef;
    if (!dbRef) {
        return false;
    }

    try {
        const folderDoc = await getDoc(doc(dbRef, folderId));

        if (!folderDoc.exists()) {
            return false;
        }

        const folder = folderDoc.data();
        if (folder.type !== "folder") {
            return false;
        }

        if (rootDir === "team" && !canUserAccessFolder(folder, user)) {
            return false;
        }

        return true;
    } catch (error) {
        console.error("Error validating explorer location:", error);
        return false;
    }
}

export async function fetchExplorerFolder({
    rootDir,
    folderId,
    homeDbRef,
    teamDbRef,
}) {
    if (!folderId) {
        return null;
    }

    const dbRef = rootDir === "personal" ? homeDbRef : teamDbRef;
    if (!dbRef) {
        return null;
    }

    const folderDoc = await getDoc(doc(dbRef, folderId));
    if (!folderDoc.exists()) {
        return null;
    }

    const folderData = folderDoc.data();
    return {
        ...folderData,
        id: folderData.id || folderDoc.id,
        rootDir,
    };
}
